import FilterSelect from "@/components/FilterSelect";
import FilterGroup from "@/components/FilterSelect/FilterGroup";
import HBCategorySelect from "@/components/HBCategorySelect";
import HBTabButton from "@/components/HBTabButton";
import Image from "@/components/Image";
import { useSearchLimit } from "@/components/LoginModal/useLoginLimit";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { SORT_ITEM_MAP, SORT_LIST } from "@/constants/sort";
import { VIEWTYPE3_ITEM_MAP, VIEWTYPE3_LIST } from "@/constants/viewType";
import useAppStore from "@/context/store";
import SearchEmpty from "@/pageComponents/Search/SearchEmpty";
import SearchBoardList from "@/pageComponents/SearchBoardList";
import SearchPinList from "@/pageComponents/SearchPinList";
import SearchUserList from "@/pageComponents/SearchUserList";
import { setSearch } from "@/tracker/shared/global";
import filterUrlParams from "@/utils/filterUrlParams";
import useABTest from "@/utils/hooks/useABtest";
import { useCloseToday } from "@/utils/hooks/useCloseToday";
import { useListenScrollDirection } from "@/utils/hooks/useListenScrollDirection";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import isDuringSpecial from "@/utils/isDuringSpecial";
import constantFactory from "@gaoding/constant-factory";
import { useMount } from "ahooks";
import { Tabs } from "antd";
import classNames from "classnames";
import Head from "next/head";
import router from "next/router";
import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import SearchMaterialList from "../../pageComponents/SearchMaterialList";
import styles from "./index.module.less";
import PageInfo from "./PageInfo";
import RecommendTopic from "./RecommendTopic";
import RecommendMaterial from "./RecommendTopic/material";
import SearchFilter from "./SearchFilter";
const TabPane = Tabs.TabPane;
const isSpecialTime = isDuringSpecial();
const MATERIAL_CATEGORY = "material_category";

const TYPE = {
  material: "material",
  pin: "pin",
  board: "board",
  user: "user",
};
const TABHEIGHT = 60;
export default function Search({
  searchResult,
  serverSideFacets,
  serverSidePinFilters,
  serverSideMaterialFilters,
  pageInfo,
  ...props
}) {
  const [visible, setVisible] = useState(true);
  const [cardType, setCardType] = useState("card");
  const [categoriesList, setCategoriesList] = useState({
    pin: [],
    board: [],
    material: [],
    user: [],
  });
  let {
    type: originalType,
    q,
    category,
    sort,
    color,
    fileType,
    material,
    ratio,
    resolution,
    filterIds,
  } = props; // 搜索类型

  const [type, setType] = useState(originalType || TYPE.pin);

  const { user, headerHeight } = useAppStore(({ user, headerHeight }) => ({
    user,
    headerHeight,
  }));

  const { onSearchLimitCheck } = useSearchLimit();

  const [recommend, setRecommend] = useState();

  const [recommendTopicClosed, onCloseRecommend] = useCloseToday(
    LOCAL_STORAGE_KEY.HB_SEARCH_RECOMMEND
  );

  const hasRecommend = useMemo(() => {
    // 是否有搜索推荐内容
    const hasContent =
      (recommend?.recommend || recommend?.recommendMaterial) &&
      (type === TYPE.pin || type === TYPE.material);

    // 有内容且没有关闭
    return hasContent && !recommendTopicClosed;
  }, [recommendTopicClosed, recommend, type]);

  const ab = useABTest("hb_search_tab_text", "text_1", {
    planA: "text_1",
    planB: "text_2",
  });

  useEffect(() => {
    const typeList = ["pin", "board", "material", "user"];
    if (typeList.includes(originalType)) {
      setType(originalType);
    } else {
      setType(TYPE.pin);
    }
  }, [originalType]);
  const onChangeTab = async (data) => {
    await onSearchLimitCheck();

    setSearch({
      search_type: "筛选器-点击",
    });
    router.push({
      pathname: "/search",
      query: { type: data.value, q },
    });
  };

  const onChange = async (data, key, isFilter) => {
    await onSearchLimitCheck();

    setSearch({
      search_type: "筛选器-点击",
    });
    const query = {
      q,
      ...(category ? { category } : null),
      ...(sort ? { sort } : null),
      ...(color ? { color } : null),
      ...(fileType ? { file_type: fileType } : null),
      ...(material ? { material: material } : null),
      ...(ratio ? { ratio } : null),
      ...(resolution ? { resolution } : null),
      ...(type ? { type } : null),
      ...(filterIds ? { filter_ids: filterIds } : null),
    };
    if (isFilter) {
      const filterIdsMap = filterIds ? filterUrlParams.decode(filterIds) : {};
      if (data?.value) {
        filterIdsMap[key] = data?.value;
      } else {
        delete filterIdsMap[key];
      }
      if (Object.keys(filterIdsMap).length > 0) {
        query.filter_ids = filterUrlParams.encode(filterIdsMap);
      } else {
        delete query.filter_ids;
      }
    } else if (data?.value) {
      query[key] = data.value;
    } else {
      delete query[key];
    }
    router.push(
      {
        pathname: "/search",
        query: query,
      },
      undefined,
      {
        shallow: router.pathname === "/search",
      }
    );
  };
  const categories = useMemo(() => {
    let result =
      categoriesList[type]?.map((i) => {
        return { ...i, key: i.id, label: i.name, value: i.value };
      }) || [];
    return constantFactory("CATEGORY", result);
  }, [categoriesList, type]);

  const selectMap = useMemo(() => {
    const filterIdsMap = filterIds ? filterUrlParams.decode(filterIds) : {};

    return {
      pin: [
        {
          name: "综合排序",
          options: SORT_LIST,
          value: SORT_ITEM_MAP[sort],
          key: "sort",
          allowClear: false,
        },
        ...(serverSidePinFilters?.map((item) => {
          const value = item?.options?.find(
            (option) => filterIdsMap[item?.key] === option?.value
          );
          return {
            ...item,
            value: value || null,
          };
        }) || []),
      ],
      board: [
        {
          name: "综合排序",
          options: SORT_LIST,
          value: SORT_ITEM_MAP[sort],
          key: "sort",
          allowClear: false,
        },
        {
          name: "分类",
          options: categories.CATEGORY_LIST,
          value: categories.CATEGORY_ITEM_MAP
            ? categories.CATEGORY_ITEM_MAP[category]
            : undefined,
          key: "category",
        },
      ],
      material: [
        {
          name: "综合排序",
          options: SORT_LIST,
          value: SORT_ITEM_MAP[sort],
          key: "sort",
          allowClear: false,
        },
        ...(serverSideMaterialFilters?.map((item) => {
          const value = item?.options?.find(
            (option) => filterIdsMap[item?.key] === option?.value
          );
          return {
            ...item,
            value: value || null,
            hidden: item.key === MATERIAL_CATEGORY,
          };
        }) || []),
      ],
      user: [
        {
          name: "视图",
          options: VIEWTYPE3_LIST,
          value: VIEWTYPE3_ITEM_MAP[cardType],
          key: "view",
          allowClear: false,
          onChange: (_) => {
            setCardType(_.value);
          },
        },
      ],
    };
  }, [
    cardType,
    filterIds,
    sort,
    serverSidePinFilters,
    categories,
    category,
    serverSideMaterialFilters,
  ]);
  // 素材分类单独取出来展开
  const materialCategoryFilter = useMemo(() => {
    return serverSideMaterialFilters?.find(
      (item) => item.key === MATERIAL_CATEGORY
    );
  }, [serverSideMaterialFilters]);

  const isVip = useIsMaterialVip();

  // 是否显示素材标识
  const isShowMaterialTag = useMemo(() => {
    if (isVip) {
      return true;
    }

    return !!selectMap?.pin?.find((item) => {
      if (item?.key === "is_material" && item?.value?.label === "仅看素材") {
        return true;
      }
    });
  }, [selectMap, isVip]);

  useEffect(() => {
    // 搜索词切换时候，需要重置搜索tab的状态
    setVisible(true);
  }, [q]);

  useListenScrollDirection(
    () => setVisible(true),
    () => {
      const height = hasRecommend ? TABHEIGHT * 2 + 378 : TABHEIGHT * 2;
      if (window.scrollY > height) {
        setVisible(false);
      }
    }
  );
  const materialCategory = useMemo(() => {
    if (type !== TYPE.material) {
      return undefined;
    }
    return selectMap[TYPE.material].find((i) => i.key === MATERIAL_CATEGORY)
      ?.value?.value;
  }, [selectMap, type]);

  const tabList = useMemo(() => {
    return [
      {
        name: "采集",
        value: "pin",
        href: `/search?type=pin&q=${q}`,
      },
      {
        name: "画板",
        value: "board",
        href: `/search?type=board&q=${q}`,
      },
      ...(isSpecialTime
        ? []
        : [
            {
              name: "用户",
              value: "user",
              href: `/search?type=user&q=${q}`,
            },
          ]),

      {
        name: (
          <>
            {ab.planA ? "商用素材" : "源文件"}
            <Image
              src="https://st0.dancf.com/static/02/202401300356-0f8c.gif"
              className={styles.rightIcon}
              alt="ic_copyright"
              width={16}
              height={16}
            />
          </>
        ),
        value: "material",
        href: `/search?type=material&q=${q}`,
        trackerProps: {
          "data-gd-click": "resource_loc_click",
          "data-gd-expose": "resource_loc_expose",
          "data-resource-type": "文字链",
          "data-resource-id": "0",
          "data-resource-title": ab.planA ? "商用素材" : "源文件",
          "data-content-source": "筛选项",
        },
      },
    ];
  }, [ab, q]);

  const is_material = recommend?.recommendType === "material";

  const searchAb = useABTest("search_ab", "new", {
    new: "new",
    old: "old",
  });

  useMount(() => {
    if (window.__NEXT_DATA__.page === "/search") {
      // 当前搜索页面刷新，触未登录搜索限制
      onSearchLimitCheck();
    }
  });

  return (
    <div className={styles.search} data-content-source="信息流">
      <Head>
        <title>{q} - 搜索结果 - 花瓣</title>
      </Head>
      {/* <div
        className={classNames([
          styles.searchHeader,
          styles.space,
          "modalOpenWidth",
        ])}
      ></div> */}

      {hasRecommend &&
        (!is_material ? (
          <RecommendTopic
            searchWord={q}
            topics={recommend}
            onClose={onCloseRecommend}
          />
        ) : (
          <RecommendMaterial
            searchWord={q}
            recommend={recommend}
            onClose={onCloseRecommend}
          />
        ))}
      <div
        className={classNames(styles.searchHeader, "modalOpenWidth", {
          [styles.active]: visible,
        })}
        style={{
          top: headerHeight,
        }}
      >
        <div className={classNames(styles.wrapper, "calculateWrapper")}>
          <HBTabButton
            className={styles.hbTab}
            options={tabList}
            onChange={onChangeTab}
            value={type}
          ></HBTabButton>
          <When condition={type === TYPE.material}>
            <div className={styles.categoryConent}>
              <HBCategorySelect
                allText={"全部"}
                minWidth={500}
                onChange={(data) => onChange(data, MATERIAL_CATEGORY, true)}
                value={materialCategory}
                options={materialCategoryFilter?.options}
              />
            </div>
          </When>
          <div className={styles.right}>
            <When condition={type !== TYPE.pin || searchAb.old}>
              <FilterGroup>
                {selectMap[type]?.map((i, index) => {
                  const select = (
                    <FilterSelect
                      onChange={(value) => {
                        onChange(value, i.key, i.isFilter);
                      }}
                      {...i}
                      key={i.key}
                      className={classNames(styles.select, {
                        [styles.selectFirst]: index === 0,
                      })}
                    />
                  );
                  if (i.options?.length && !i.hidden) {
                    return select;
                  }
                  return null;
                })}
              </FilterGroup>
            </When>

            <When
              condition={type === TYPE.pin && !searchAb.loading && searchAb.new}
            >
              <SearchFilter
                className={styles.filter}
                filters={selectMap[type]}
                onChange={onChange}
              />
            </When>
          </div>
        </div>
      </div>
      {pageInfo && <PageInfo {...pageInfo} />}
      <Tabs
        activeKey={type}
        destroyInactiveTabPane={true}
        onChange={onChangeTab}
        className={classNames(styles.tabs, "calculateWrapper", "wrapper")}
        // tabBarStyle={{ top: `${height}px`,transform:'.5' }}
        centered
      >
        <TabPane tab="采集" key={TYPE.pin}>
          <SearchPinList
            query={props}
            searchResult={searchResult}
            serverSideFacets={serverSideFacets}
            serverSidePinFilters={serverSidePinFilters}
            type={type}
            showMaterialTag={isShowMaterialTag}
            setRecommend={setRecommend}
          />
        </TabPane>
        <TabPane tab="画板" key={TYPE.board}>
          <SearchBoardList
            setCategoriesList={setCategoriesList}
            query={props}
            empty={<SearchEmpty />}
            searchResult={searchResult}
            serverSideFacets={serverSideFacets}
          />
        </TabPane>
        <TabPane tab="素材" key={TYPE.material}>
          <SearchMaterialList
            query={props}
            empty={<SearchEmpty />}
            searchResult={searchResult}
            serverSideMaterialFilters={serverSideMaterialFilters}
            setRecommend={setRecommend}
          />
        </TabPane>
        {!isSpecialTime ? (
          <TabPane tab="用户" key={TYPE.user}>
            <SearchUserList
              query={props}
              cardType={cardType}
              empty={<SearchEmpty />}
              searchResult={searchResult}
            />
          </TabPane>
        ) : null}
      </Tabs>
    </div>
  );
}
